import { SNAP_SIXTEENTHS } from "../client/interpolator.ts";
import { SPEED_UNITS, diagonalUnits, mostCover } from "../movement/true-point.ts";
import { COLLISION_CELL, FOOT_HEIGHT } from "../movement/step.ts";
import { MOVE_BUDGET_CAP, QUARTER_RATE_TICKS } from "../protocol/limits.ts";
import { report } from "./checks.ts";
import type { Level, RulesProblem } from "./types.ts";

/**
 * The checks that read the run's constants against one another, against the code's own distances and against the
 * levels (ADR-116 decisions 2 and 27): each constant's own bounds are its record's, and these run across the tables
 * after their records, reporting on the constant they refuse.
 */

/**
 * Checks `run_speed` (ADR-116 decision 27 and the amendment of 2026-10-07): above `walk_speed`, since a run is faster
 * than a walk; the most a run covers in the steps a character may take between two quarter-rate views, whatever
 * offsets it starts with (`mostCover`), no more than the snap distance, or a distant runner would teleport on a
 * watcher's page; and its longest step below the feet box's height and a collision cell, or a step could
 * pass over a blocked cell. A speed its own record refused is not checked again.
 *
 * @param walkSpeed The checked `walk_speed` in speed units, or null when the constants hold none that passed.
 * @param runSpeed The checked `run_speed`, or null likewise.
 * @param problems Where problems go.
 */
export function checkRunSpeed(walkSpeed: number | null, runSpeed: number | null, problems: RulesProblem[]): void {
  if (runSpeed === null) {
    return;
  }
  const place = { table: "constants", id: "run_speed", problems } as const;
  if (walkSpeed !== null && runSpeed <= walkSpeed) {
    report(place, `count is ${runSpeed}, and it must be above walk_speed, ${walkSpeed}: a run is faster than a walk`);
  }
  const steps = QUARTER_RATE_TICKS + MOVE_BUDGET_CAP - 1;
  const covered = mostCover(runSpeed, steps);
  if (covered > SNAP_SIXTEENTHS) {
    report(place, `count is ${runSpeed}: the ${steps} steps a character may take between two quarter-rate views would cover up to ${covered} sixteenths, past the ${SNAP_SIXTEENTHS} at which a watcher's page snaps`);
  }
  const longest = Math.ceil(runSpeed / SPEED_UNITS);
  if (longest >= FOOT_HEIGHT + COLLISION_CELL) {
    report(place, `count is ${runSpeed}: its longest step, ${longest} sixteenths, is not below the feet box's height and a collision cell, ${FOOT_HEIGHT + COLLISION_CELL}, so a step could pass over a blocked cell`);
  }
}

/**
 * Checks the diagonal's share of each speed (ADR-116 decisions 25 and 31 and the amendment of 2026-10-07). Each axis
 * of a diagonal moves the share (`diagonalUnits`), the speed over root two rounded down. A share under one sixteenth a
 * step at the walk or at a creature's speed is refused: a creature steering along a diagonal would step nothing, and
 * a walking character would on some ticks. The walk's least is then 23 speed units and a creature's 32. No speed has
 * a diagonal faster than its cardinal, since the share is rounded down (`movement/true-point.test.ts`), so there is
 * nothing of that to refuse. A speed its own record refused is not checked again.
 *
 * @param walkSpeed The checked `walk_speed` in speed units, or null when the constants hold none that passed.
 * @param creatureSpeed The checked `creature_speed`, or null likewise.
 * @param problems Where problems go.
 */
export function checkDiagonalMoves(walkSpeed: number | null, creatureSpeed: number | null, problems: RulesProblem[]): void {
  if (walkSpeed !== null && diagonalUnits(walkSpeed) < SPEED_UNITS) {
    report({ table: "constants", id: "walk_speed", problems }, `count is ${walkSpeed}: a diagonal's share of it is ${diagonalUnits(walkSpeed)} speed units, under the ${SPEED_UNITS} of one sixteenth a step, so a character walking a diagonal would not move every tick`);
  }
  if (creatureSpeed !== null && diagonalUnits(creatureSpeed) < SPEED_UNITS) {
    report({ table: "constants", id: "creature_speed", problems }, `count is ${creatureSpeed}: a diagonal's share of it is ${diagonalUnits(creatureSpeed)} speed units, under the ${SPEED_UNITS} of one sixteenth a step, so a creature steering along a diagonal would not move`);
  }
}

/**
 * Checks `run_restart` against the levels (ADR-116 decision 2): at most the smallest Stamina pool any level gives, or
 * a character that ran itself empty could never hold enough to run again.
 *
 * @param restart The checked `run_restart`, or null when the constants hold none that passed.
 * @param levels The levels that passed their checks.
 * @param problems Where problems go.
 */
export function checkRunRestart(restart: number | null, levels: readonly Level[], problems: RulesProblem[]): void {
  if (restart === null || levels.length === 0) {
    return;
  }
  let smallest = Number.POSITIVE_INFINITY;
  for (const level of levels) {
    smallest = Math.min(smallest, level.pools.stamina);
  }
  if (restart > smallest) {
    report({ table: "constants", id: "run_restart", problems }, `count is ${restart}, and it must be at most ${smallest}, the smallest Stamina pool of the levels: a character that ran itself empty could never run again`);
  }
}
